import React, { useState, useEffect, useMemo, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import Navbar from './components/Navbar';
import InvalidLicensesAlertBar from './components/InvalidLicensesAlertBar';
import LoadingSpinner from './components/LoadingSpinner';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import AnimalsPage from './pages/AnimalsPage';
import CalendarPage from './pages/CalendarPage';
import AvailabilityPage from './pages/AvailabilityPage';
import PaymentsPage from './pages/PaymentsPage';
import StatisticsPage from './pages/StatisticsPage';
import AccountPage from './pages/AccountPage';
import SettingsPage from './pages/SettingsPage';
import WeatherPage from './pages/WeatherPage';
import HelpPage from './pages/HelpPage';
import StudentsPage from './pages/StudentsPage';
import CalendarProvasPage from './pages/CalendarProvasPage';
import RankingPage from './pages/RankingPage';
import LicencasPage from './pages/LicencasPage';
import type { Licenca } from './pages/LicencasPage';

import { useAuth } from './contexts/AuthContext';
import './styles/theme.css';
import { apiCall, apiConfig } from './config/api';
import Toast from './components/Toast';
import ChangelogModal from './components/Changelog';

export const ToastContext = createContext({ showToast: (msg: string, type?: 'success' | 'error' | 'info') => {} });
export const LoginResponseContext = createContext({ 
  setLoginResponse: (response: { showChangelog?: boolean } | null) => {} 
});

// Interface para as aulas
interface Lesson {
  id: number;
  user_name: string;
  animal_name: string;
  date: string;
  start_time: string;
  end_time: string;
  observations?: string;
}

// Componente para mostrar as próximas aulas
const NextLessons: React.FC<{ onOpenICSModal: () => void }> = ({ onOpenICSModal }) => {
  const { user } = useAuth();
  const [allLessons, setAllLessons] = useState<Lesson[]>([]);
  const [displayedLessons, setDisplayedLessons] = useState<Lesson[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const lessonsPerPage = 5;

  useEffect(() => {
    const loadNextLessons = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        setError('');
        
        // Buscar todas as aulas
        const response = await apiCall(user.role === 'trainer' ? '/bookings/all' : '/bookings/my');
        const allBookings = response.bookings || [];
        
        const now = new Date();
        const today = now.toISOString().split('T')[0];
        
        // Filtrar aulas futuras e atuais
        const futureLessons = allBookings.filter((booking: any) => {
          const lessonDate = booking.date;
          const lessonTime = booking.start_time;
          const lessonEndTime = booking.end_time;
          const lessonStartDateTime = new Date(`${lessonDate}T${lessonTime}`);
          const lessonEndDateTime = new Date(`${lessonDate}T${lessonEndTime}`);
          
          // Verificar se a aula está acontecendo agora
          const isCurrentLesson = now >= lessonStartDateTime && now <= lessonEndDateTime;
          
          // Para trainers: mostrar aulas atuais e próximas 3 aulas
          // Para students: mostrar apenas suas aulas atuais e futuras
          if (user.role === 'trainer') {
            return lessonStartDateTime >= now || isCurrentLesson;
          } else {
            // Para students, só mostrar se for a aula deles
            return (lessonStartDateTime >= now || isCurrentLesson) && booking.user_id === user.id;
          }
        }).sort((a: any, b: any) => {
          const aDateTime = new Date(`${a.date}T${a.start_time}`);
          const bDateTime = new Date(`${b.date}T${b.start_time}`);
          return aDateTime.getTime() - bDateTime.getTime();
        });
        
        // Para trainers e students, mostrar todas as aulas filtradas
        setAllLessons(futureLessons);
      } catch (err: any) {
        setError(err.message || 'Erro ao carregar próximas aulas');
      } finally {
        setLoading(false);
      }
    };
    
    loadNextLessons();
    setCurrentPage(1); // Reset para a primeira página quando o utilizador muda
  }, [user]);

  // Calcular aulas a serem exibidas baseado na página atual
  useEffect(() => {
    const startIndex = 0;
    const endIndex = currentPage * lessonsPerPage;
    setDisplayedLessons(allLessons.slice(startIndex, endIndex));
  }, [allLessons, currentPage]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) {
      return 'Hoje';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return 'Amanhã';
    } else {
      return date.toLocaleDateString('pt-BR', { 
        weekday: 'long', 
        day: 'numeric', 
        month: 'long' 
      });
    }
  };

  const formatTime = (timeStr: string) => {
    return timeStr.slice(0, 5); // Remove segundos se existirem
  };

  const isCurrentLesson = (lesson: Lesson) => {
    const now = new Date();
    const lessonStart = new Date(`${lesson.date}T${lesson.start_time}`);
    const lessonEnd = new Date(`${lesson.date}T${lesson.end_time}`);
    return now >= lessonStart && now <= lessonEnd;
  };

  const loadMoreLessons = () => {
    setCurrentPage(prev => prev + 1);
  };

  const hasMoreLessons = displayedLessons.length < allLessons.length;

  const headerRight = (
    <div className="d-flex align-items-center gap-2">
      <Link to="/calendar" className="btn btn-sm btn-outline-light" style={{ border: 'none' }}>
        Ver todas
      </Link>
      {user?.role === 'student' && displayedLessons.length > 0 && (
        <button
          className="btn btn-sm btn-outline-light"
          style={{ border: 'none' }}
          title="Adicionar ao meu calendário"
          onClick={onOpenICSModal}
        >
          <i className="fas fa-calendar-plus me-1"></i>
        </button>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center" style={{backgroundColor: '#a2449c', color: 'white'}}>
          <h5 className="mb-0">
            <i className="fas fa-calendar-check me-2"></i>
            Próximas Aulas
          </h5>
          <Link to="/calendar" className="btn btn-sm btn-outline-light" style={{ border: 'none' }}>
            Ver todas
          </Link>
        </div>
        <div className="card-body text-center py-4">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center" style={{backgroundColor: '#a2449c', color: 'white'}}>
          <h5 className="mb-0">
            <i className="fas fa-calendar-check me-2"></i>
            Próximas Aulas
          </h5>
          <Link to="/calendar" className="btn btn-sm btn-outline-light" style={{ border: 'none' }}>
            Ver todas
          </Link>
        </div>
        <div className="card-body text-center py-3">
          <p className="text-muted mb-0">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card shadow-sm">
      <div className="card-header d-flex justify-content-between align-items-center" style={{backgroundColor: '#a2449c', color: 'white'}}>
        <h5 className="mb-0">
          <i className="fas fa-calendar-check me-2"></i>
          Próximas Aulas
        </h5>
        {headerRight}
      </div>
      <div className="card-body">
        {displayedLessons.length === 0 ? (
          <div className="text-center py-3">
            <i className="fas fa-calendar-times fa-2x mb-2" style={{color: '#a2449c'}}></i>
            <p className="text-muted mb-0">
              {user?.role === 'trainer' 
                ? 'Não há próximas aulas marcadas.' 
                : 'Não tem aulas marcadas no futuro.'}
            </p>
          </div>
        ) : (
          <>
            <div className="row">
              {displayedLessons.map((lesson: Lesson, index: number) => (
                <div key={lesson.id} className="col-12 mb-3">
                  <div className={`card border-0 ${isCurrentLesson(lesson) ? 'border-primary' : ''}`} 
                       style={{backgroundColor: isCurrentLesson(lesson) ? '#f8f9fa' : 'white'}}>
                    <div className="card-body p-3">
                      <div className="d-flex align-items-center mb-1">
                        <i className="fas fa-clock me-2" style={{color: '#a2449c'}}></i>
                        <span className="fw-bold">{formatDate(lesson.date)} às {formatTime(lesson.start_time)}</span>
                        {isCurrentLesson(lesson) && (
                          <span className="badge badge-agora-ex ms-2">Agora</span>
                        )}
                      </div>
                      {user?.role === 'trainer' ? (
                        <div className="text-muted">
                          <i className="fas fa-user me-1"></i>
                          {lesson.user_name}<span style={{fontSize: '0.7rem'}}> com </span>{lesson.animal_name}
                        </div>
                      ) : (
                        <div className="text-muted">
                          <i className="fas fa-paw me-1"></i>
                          {lesson.animal_name}
                        </div>
                      )}
                      {lesson.observations && (
                        <div className="small text-muted mt-1">
                          <i className="fas fa-sticky-note me-1"></i>
                          {lesson.observations}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {hasMoreLessons && (
              <div className="text-center mt-3">
                <button 
                  className="btn btn-outline-primary" 
                  onClick={loadMoreLessons}
                  style={{borderColor: '#a2449c', color: '#a2449c'}}
                >
                  <i className="fas fa-plus me-2"></i>
                  Ver mais ({allLessons.length - displayedLessons.length} restantes)
                </button>
              </div>
            )}
          </>
        )}
      </div>
      
    </div>
  );
};

// Interface para prova (resumo no card; campos extra para a modal de detalhe)
interface ProvaResumo {
  data: string;
  dataOriginal?: string;
  nomeClube: string;
  anulada: boolean;
  tipoProva: string;
  local: string;
  indoorOutdoor: string;
  juiz: string;
  tipoPiso: string;
  responsavel?: string;
  email?: string;
  condicoes?: string;
  tipoObstaculosContactos?: string;
  materialPneu?: string;
  materialSaltoComprimento?: string;
  materialMuro?: string;
  materialSaltos?: string;
}

const NEXT_PROVAS_MAX_DAYS_GROUP = 3;
type NextProvasFieldKey = 'nomeClube' | 'anulada' | 'indoorOutdoor' | 'tipoProva' | 'local' | 'tipoPiso' | 'juiz';

function nextProvasGetField(p: ProvaResumo, key: NextProvasFieldKey): string {
  if (key === 'anulada') return p.anulada ? 'Sim' : 'Não';
  const v = (p as unknown as Record<string, unknown>)[key];
  return (v != null && String(v).trim() !== '') ? String(v).trim() : '';
}

function nextProvasNormalize(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/\p{M}/gu, '').replace(/[^a-z0-9]/g, '');
}

/** Verifica se duas datas (YYYY-MM-DD) são dias consecutivos (b é o dia a seguir a a). */
function nextProvasIsNextDay(prev: string, curr: string): boolean {
  const d1 = new Date(prev + 'T12:00:00').getTime();
  const d2 = new Date(curr + 'T12:00:00').getTime();
  const diff = Math.round((d2 - d1) / (1000 * 60 * 60 * 24));
  return diff === 1;
}

interface NextProvasGroup {
  provas: ProvaResumo[];
  common: Partial<Record<NextProvasFieldKey, string>>;
  differing: Array<{ data: string; dataShort: string; values: Record<NextProvasFieldKey, string> }>;
}

// Componente para mostrar as próximas provas na Home
const NextProvas: React.FC = () => {
  const [allProvas, setAllProvas] = useState<ProvaResumo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [displayCount] = useState(1);
  const [modalGroup, setModalGroup] = useState<NextProvasGroup | null>(null);
  const [showProvasICSModal, setShowProvasICSModal] = useState(false);

  useEffect(() => {
    const loadProvas = async () => {
      try {
        setLoading(true);
        setError('');
        const res = await apiCall('/calendar-provas', { method: 'GET' });
        const provas: ProvaResumo[] = (res.provas || []).map((p: any) => ({
          data: p.data,
          dataOriginal: p.dataOriginal,
          nomeClube: p.nomeClube,
          anulada: p.anulada,
          tipoProva: p.tipoProva,
          local: p.local,
          indoorOutdoor: p.indoorOutdoor,
          juiz: p.juiz,
          tipoPiso: p.tipoPiso,
          responsavel: p.responsavel,
          email: p.email,
          condicoes: p.condicoes,
          tipoObstaculosContactos: p.tipoObstaculosContactos,
          materialPneu: p.materialPneu,
          materialSaltoComprimento: p.materialSaltoComprimento,
          materialMuro: p.materialMuro,
          materialSaltos: p.materialSaltos,
        }));
        const today = new Date().toISOString().split('T')[0];
        const futuras = provas.filter((p) => p.data >= today && !p.anulada);
        setAllProvas(futuras);
      } catch (err: any) {
        setError(err.message || 'Erro ao carregar próximas provas');
        setAllProvas([]);
      } finally {
        setLoading(false);
      }
    };
    loadProvas();
  }, []);

  const formatDateLabel = (data: string) => {
    const [y, m, d] = data.split('-');
    const date = new Date(parseInt(y!, 10), parseInt(m!, 10) - 1, parseInt(d!, 10));
    const monthShort = date.toLocaleDateString('pt-PT', { month: 'short' }).replace(/\.$/, '');
    return `${date.getDate()} ${monthShort.charAt(0).toUpperCase() + monthShort.slice(1)}`;
  };

  const formatDateRangeShort = (first: string, last: string) => {
    const [y1, m1, d1] = first.split('-');
    const [y2, m2, d2] = last.split('-');
    const dateFirst = new Date(parseInt(y1!, 10), parseInt(m1!, 10) - 1, parseInt(d1!, 10));
    const dateLast = new Date(parseInt(y2!, 10), parseInt(m2!, 10) - 1, parseInt(d2!, 10));
    const monthShort = dateLast.toLocaleDateString('pt-PT', { month: 'short' }).replace(/\.$/, '');
    const monthCap = monthShort.charAt(0).toUpperCase() + monthShort.slice(1);
    return `${dateFirst.getDate()} a ${dateLast.getDate()} ${monthCap}`;
  };

  // Mesma lógica do Calendário de Provas: agrupar por mesmo clube + dias consecutivos (máx. 3)
  const groups: NextProvasGroup[] = React.useMemo(() => {
    const list = [...allProvas].sort((a, b) => {
      const byClub = (a.nomeClube || '').localeCompare(b.nomeClube || '', 'pt-PT');
      if (byClub !== 0) return byClub;
      return a.data.localeCompare(b.data);
    });
    if (list.length === 0) return [];
    const keys: NextProvasFieldKey[] = ['nomeClube', 'anulada', 'indoorOutdoor', 'tipoProva', 'local', 'tipoPiso', 'juiz'];

    function buildGroup(provas: ProvaResumo[]): NextProvasGroup {
      const common: Partial<Record<NextProvasFieldKey, string>> = {};
      const formatDayShort = (data: string) => {
        const [y, m, d] = data.split('-');
        const date = new Date(parseInt(y!, 10), parseInt(m!, 10) - 1, parseInt(d!, 10));
        return date.toLocaleDateString('pt-PT', { day: '2-digit', month: 'short' });
      };
      const differing = provas.map((p) => ({
        data: p.data,
        dataShort: formatDayShort(p.data),
        values: keys.reduce((acc, k) => ({ ...acc, [k]: nextProvasGetField(p, k) }), {} as Record<NextProvasFieldKey, string>),
      }));
      keys.forEach((key) => {
        const first = nextProvasGetField(provas[0], key);
        const firstNorm = nextProvasNormalize(first);
        const allSame = firstNorm === '' || provas.every((p) => nextProvasNormalize(nextProvasGetField(p, key)) === firstNorm);
        if (allSame && first !== '') common[key] = first;
      });
      return { provas, common, differing };
    }

    const result: NextProvasGroup[] = [];
    let current: ProvaResumo[] = [list[0]];
    const sameClub = (a: ProvaResumo, b: ProvaResumo) =>
      nextProvasNormalize(a.nomeClube || '') === nextProvasNormalize(b.nomeClube || '');

    for (let i = 1; i < list.length; i++) {
      const prev = current[current.length - 1];
      const curr = list[i];
      const canExtend =
        sameClub(prev, curr) &&
        current.length < NEXT_PROVAS_MAX_DAYS_GROUP &&
        nextProvasIsNextDay(prev.data, curr.data);
      if (canExtend) {
        current.push(curr);
      } else {
        result.push(buildGroup(current));
        current = [curr];
      }
    }
    result.push(buildGroup(current));
    result.sort((g1, g2) => {
      const byDate = g1.provas[0].data.localeCompare(g2.provas[0].data);
      if (byDate !== 0) return byDate;
      return (g1.provas[0].nomeClube || '').localeCompare(g2.provas[0].nomeClube || '', 'pt-PT');
    });
    return result;
  }, [allProvas]);

  const displayedGroups = groups.slice(0, displayCount);

  const provasHeaderRight = (
    <div className="d-flex align-items-center gap-2">
      <Link to="/calendar-provas" className="btn btn-sm btn-outline-light" style={{ border: 'none' }}>
        Ver todas
      </Link>
      <button
        className="btn btn-sm btn-outline-light"
        style={{ border: 'none' }}
        title="Adicionar calendário de provas ao meu calendário"
        onClick={() => setShowProvasICSModal(true)}
      >
        <i className="fas fa-calendar-plus me-1"></i>
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: '#a2449c', color: 'white' }}>
          <h5 className="mb-0">
            <i className="fas fa-calendar-alt me-2"></i>
            Próximas Provas
          </h5>
          {provasHeaderRight}
        </div>
        <div className="card-body text-center py-4">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: '#a2449c', color: 'white' }}>
          <h5 className="mb-0">
            <i className="fas fa-calendar-alt me-2"></i>
            Próximas Provas
          </h5>
          {provasHeaderRight}
        </div>
        <div className="card-body text-center py-3">
          <p className="text-muted mb-0">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="card shadow-sm">
      <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: '#a2449c', color: 'white' }}>
        <h5 className="mb-0">
          <i className="fas fa-calendar-alt me-2"></i>
          Próximas Provas
        </h5>
        {provasHeaderRight}
      </div>
      <div className="card-body">
        {displayedGroups.length === 0 ? (
          <div className="text-center py-3">
            <i className="fas fa-calendar-times fa-2x mb-2" style={{ color: '#a2449c' }}></i>
            <p className="text-muted mb-0">Não há próximas provas no calendário.</p>
          </div>
        ) : (
          <>
            <div className="row">
              {displayedGroups.map((group, groupIdx) => {
                const isSingle = group.provas.length === 1;
                const first = group.provas[0];
                const last = group.provas[group.provas.length - 1];
                const year = first.data.substring(0, 4);
                const dateLabel = isSingle ? formatDateLabel(first.data) : formatDateRangeShort(first.data, last.data);
                const keys: NextProvasFieldKey[] = ['nomeClube', 'anulada', 'indoorOutdoor', 'tipoProva', 'local', 'tipoPiso', 'juiz'];

                return (
                  <div key={`next-${first.data}-${first.nomeClube}-${groupIdx}`} className="col-12 mb-3">
                    <div
                      className="card border-0 shadow-sm"
                      style={{ backgroundColor: '#f8f9fa', cursor: 'pointer' }}
                      role="button"
                      tabIndex={0}
                      onClick={() => setModalGroup(group)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setModalGroup(group); } }}
                    >
                      <div className="card-body p-3">
                        <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                          <div className="d-flex align-items-center flex-wrap gap-2 min-w-0">
                            <span className="fw-bold fs-6" style={{ color: '#a2449c' }}>
                              {isSingle ? first.nomeClube : (group.common.nomeClube || first.nomeClube)}
                            </span>
                            {(isSingle ? first.anulada : group.common.anulada === 'Sim') && (
                              <span className="badge bg-danger">Anulada</span>
                            )}
                            {(isSingle ? first.indoorOutdoor : group.common.indoorOutdoor) && (
                              <span className="badge bg-light text-dark border">
                                {isSingle ? first.indoorOutdoor : group.common.indoorOutdoor}
                              </span>
                            )}
                          </div>
                          <div className="text-end flex-shrink-0">
                            <div className="small text-uppercase fw-bold text-secondary">{dateLabel}</div>
                            <div className="small text-muted" style={{ fontSize: '0.75rem' }}>{year}</div>
                          </div>
                        </div>
                        {isSingle ? (
                          <>
                            {first.tipoProva && <div className="small text-muted mb-1">{first.tipoProva}</div>}
                            {first.local && (
                              <div className="small mt-1">
                                <i className="fas fa-map-marker-alt me-1 text-muted"></i>
                                {first.local}
                              </div>
                            )}
                            {first.tipoPiso && (
                              <div className="small">
                                <i className="fas fa-layer-group me-1 text-muted"></i>
                                Tipo de piso: {first.tipoPiso}
                              </div>
                            )}
                            {first.juiz && (
                              <div className="small">
                                <i className="fas fa-gavel me-1 text-muted"></i>
                                Juiz: {first.juiz}
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            {group.common.tipoProva && <div className="small text-muted mb-1">{group.common.tipoProva}</div>}
                            {group.common.local && (
                              <div className="small mt-1">
                                <i className="fas fa-map-marker-alt me-1 text-muted"></i>
                                {group.common.local}
                              </div>
                            )}
                            {group.common.tipoPiso && (
                              <div className="small">
                                <i className="fas fa-layer-group me-1 text-muted"></i>
                                Tipo de piso: {group.common.tipoPiso}
                              </div>
                            )}
                            {group.common.juiz && (
                              <div className="small">
                                <i className="fas fa-gavel me-1 text-muted"></i>
                                Juiz: {group.common.juiz}
                              </div>
                            )}
                            {keys.map((key) => {
                              if (group.common[key] !== undefined) return null;
                              const hasAny = group.differing.some((d) => d.values[key] !== '');
                              if (!hasAny) return null;
                              const labels: Record<NextProvasFieldKey, string> = {
                                nomeClube: 'Clube', anulada: 'Anulada', indoorOutdoor: 'Indoor/Outdoor',
                                tipoProva: 'Tipo de prova', local: 'Local', tipoPiso: 'Tipo de piso', juiz: 'Juiz',
                              };
                              return (
                                <div key={key} className="mt-2 small">
                                  <div className="text-muted fw-bold mb-1">{labels[key]}</div>
                                  <div className="ps-2 border-start border-2" style={{ borderColor: '#dee2e6' }}>
                                    {group.differing.map((d) => (
                                      <div key={d.data} className="mb-1">
                                        <span className="text-muted">{d.dataShort}:</span> {d.values[key]}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {modalGroup && createPortal((() => {
              const formatDayShort = (data: string) => {
                const [y, m, d] = data.split('-');
                const date = new Date(parseInt(y!, 10), parseInt(m!, 10) - 1, parseInt(d!, 10));
                return date.toLocaleDateString('pt-PT', { day: '2-digit', month: 'short' });
              };
              const getProvaValue = (p: ProvaResumo, key: string): string => {
                if (key === 'data') {
                  if (p.dataOriginal && p.dataOriginal.trim()) return p.dataOriginal.trim();
                  const [y, m, d] = p.data.split('-');
                  return new Date(parseInt(y!, 10), parseInt(m!, 10) - 1, parseInt(d!, 10)).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' });
                }
                if (key === 'anulada') return p.anulada ? 'Sim' : 'Não';
                const v = (p as unknown as Record<string, unknown>)[key];
                return (v != null && String(v).trim() !== '') ? String(v).trim() : '';
              };
              const normalizeForCompare = (s: string) => s.toLowerCase().normalize('NFD').replace(/\p{M}/gu, '').replace(/[^a-z0-9]/g, '');
              const MODAL_KEYS: { key: string; label: string }[] = [
                { key: 'data', label: 'Data' },
                { key: 'nomeClube', label: 'Nome do clube' },
                { key: 'responsavel', label: 'Nome do responsável' },
                { key: 'email', label: 'Email' },
                { key: 'tipoProva', label: 'Nº e tipo de prova (Campeonato/Open/Seletiva)' },
                { key: 'local', label: 'Local da prova' },
                { key: 'indoorOutdoor', label: 'Indoor ou outdoor' },
                { key: 'condicoes', label: 'Condições envolventes (WC, Água, Comida, Dormida)' },
                { key: 'juiz', label: 'Juiz' },
                { key: 'tipoPiso', label: 'Tipo de piso' },
                { key: 'tipoObstaculosContactos', label: 'Tipo obstáculos contactos' },
                { key: 'materialPneu', label: 'Material do pneu' },
                { key: 'materialSaltoComprimento', label: 'Material do salto comprimento' },
                { key: 'materialMuro', label: 'Material do muro' },
                { key: 'materialSaltos', label: 'Material dos saltos' },
              ];
              const provas = modalGroup.provas;
              const isSingle = provas.length === 1;

              if (isSingle) {
                const p = provas[0];
                return (
                  <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => setModalGroup(null)} role="dialog" aria-modal="true" aria-labelledby="nextModalLabel">
                    <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg" onClick={(e) => e.stopPropagation()}>
                      <div className="modal-content">
                        <div className="modal-header border-0 pb-0">
                          <h5 className="modal-title" id="nextModalLabel" style={{ color: '#a2449c' }}><i className="fas fa-info-circle me-2"></i>Detalhe da prova</h5>
                          <button type="button" className="btn-close" aria-label="Fechar" onClick={() => setModalGroup(null)} />
                        </div>
                        <div className="modal-body pt-0">
                          {p.anulada && <div className="mb-2"><span className="badge bg-danger">Anulada</span></div>}
                          <dl className="row small mb-0">
                            {MODAL_KEYS.map(({ key, label }) => {
                              const value = getProvaValue(p, key);
                              if (!value) return null;
                              return (
                                <React.Fragment key={key}>
                                  <dt className="col-sm-4 text-muted fw-normal">{label}</dt>
                                  <dd className="col-sm-8 mb-2">{key === 'email' ? <a href={`mailto:${value}`}>{value}</a> : value}</dd>
                                </React.Fragment>
                              );
                            })}
                          </dl>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }

              const common: Record<string, string> = {};
              const differing: Record<string, Array<{ dataShort: string; value: string }>> = {};
              MODAL_KEYS.forEach(({ key }) => {
                const values = provas.map((p) => getProvaValue(p, key));
                const normalized = values.map((v) => normalizeForCompare(v));
                const allSame = normalized.every((n) => n === normalized[0]) && normalized[0] !== '';
                if (allSame && values[0]) common[key] = values[0];
                else differing[key] = provas.map((p) => ({ dataShort: formatDayShort(p.data), value: getProvaValue(p, key) })).filter((d) => d.value);
              });
              const hasAnulada = provas.some((p) => p.anulada);
              return (
                <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => setModalGroup(null)} role="dialog" aria-modal="true" aria-labelledby="nextModalLabel">
                  <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-content">
                      <div className="modal-header border-0 pb-0">
                        <h5 className="modal-title" id="nextModalLabel" style={{ color: '#a2449c' }}><i className="fas fa-info-circle me-2"></i>Detalhe da prova</h5>
                        <button type="button" className="btn-close" aria-label="Fechar" onClick={() => setModalGroup(null)} />
                      </div>
                      <div className="modal-body pt-0">
                        {hasAnulada && <div className="mb-2"><span className="badge bg-danger">Anulada</span></div>}
                        {Object.keys(common).length > 0 && (
                          <div className="mb-3">
                            <div className="small text-muted mb-2">De {getProvaValue(provas[0], 'data')} a {getProvaValue(provas[provas.length - 1], 'data')}</div>
                            <dl className="row small mb-0">
                              {MODAL_KEYS.filter(({ key }) => common[key] && key !== 'data').map(({ key, label }) => (
                                <React.Fragment key={key}>
                                  <dt className="col-sm-4 text-muted fw-normal">{label}</dt>
                                  <dd className="col-sm-8 mb-2">{key === 'email' ? <a href={`mailto:${common[key]}`}>{common[key]}</a> : common[key]}</dd>
                                </React.Fragment>
                              ))}
                            </dl>
                          </div>
                        )}
                        {MODAL_KEYS.some(({ key }) => key !== 'data' && differing[key]?.length) && (
                          <div>
                            <h6 className="text-muted small text-uppercase fw-bold mb-2">Por data</h6>
                            {MODAL_KEYS.filter(({ key }) => key !== 'data' && differing[key]?.length).map(({ key, label }) => (
                              <div key={key} className="mb-3 small">
                                <div className="text-muted fw-bold mb-1">{label}</div>
                                <div className="ps-2 border-start border-2" style={{ borderColor: '#dee2e6' }}>
                                  {differing[key].map((d, i) => (
                                    <div key={i} className="mb-1">
                                      <span className="text-muted">{d.dataShort}:</span> {key === 'email' ? <a href={`mailto:${d.value}`}>{d.value}</a> : d.value}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })(), document.body)}
          </>
        )}
      </div>
    </div>

    {showProvasICSModal && (
      <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 9999 }}>
        <div className="modal-dialog modal-lg" style={{ marginTop: '5vh' }}>
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-calendar-plus me-2"></i>
                Como adicionar o calendário de provas
              </h5>
              <button type="button" className="btn-close" onClick={() => setShowProvasICSModal(false)} aria-label="Fechar"></button>
            </div>
            <div className="modal-body">
              <div className="alert alert-info mb-3">
                <i className="fas fa-info-circle me-2"></i>
                Este calendário é <strong>comum a todos os utilizadores</strong> e inclui todas as provas do calendário oficial.
              </div>
              <div className="row">
                <div className="col-md-6">
                  <h6 className="text-primary mb-3">
                    <i className="fas fa-download me-2"></i>
                    Download
                  </h6>
                  <ol className="mb-4">
                    <li>Clique em &quot;Descarregar Calendário&quot; em baixo</li>
                    <li>O ficheiro .ics será descarregado</li>
                    <li>Abra o ficheiro no seu calendário (Google, Apple, Outlook, etc.)</li>
                  </ol>
                </div>
                <div className="col-md-6">
                  <h6 className="text-success mb-3">
                    <i className="fas fa-sync me-2"></i>
                    Subscrição (recomendado)
                  </h6>
                  <p className="mb-3">Para ter o calendário sempre atualizado, copie o link e adicione como calendário subscrito:</p>
                  <div className="input-group mb-3">
                    <input type="text" className="form-control" value={`${apiConfig.baseURL}/calendar-provas.ics`} readOnly />
                    <button
                      className="btn btn-outline-primary"
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(`${apiConfig.baseURL}/calendar-provas.ics`);
                        const toast = document.createElement('div');
                        toast.className = 'alert alert-success position-fixed';
                        toast.style.cssText = 'top: 20px; right: 20px; z-index: 9999;';
                        toast.textContent = 'URL copiada para a área de transferência!';
                        document.body.appendChild(toast);
                        setTimeout(() => document.body.removeChild(toast), 3000);
                      }}
                    >
                      <i className="fas fa-copy"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowProvasICSModal(false)}>Fechar</button>
              <a
                href={`${apiConfig.baseURL}/calendar-provas.ics`}
                className="btn btn-primary"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setShowProvasICSModal(false)}
              >
                <i className="fas fa-calendar-plus me-2"></i>
                Descarregar Calendário
              </a>
            </div>
          </div>
        </div>
      </div>
    )}
  </>
  );
};

// Bloco Licenças (apenas animais do utilizador logado) — só aparece se houver licenças
const LicencasUserBlock: React.FC = () => {
  const [licencas, setLicencas] = useState<Licenca[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const res = await apiCall('/licencas/my', { method: 'GET' });
        setLicencas(res.licencas || []);
      } catch (err: any) {
        setError(err.message || 'Erro ao carregar licenças.');
        setLicencas([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: '#a2449c', color: 'white' }}>
          <h5 className="mb-0">
            <i className="fas fa-id-card me-2"></i>
            Licenças
          </h5>
          <Link to="/licencas" className="btn btn-sm btn-outline-light" style={{ border: 'none' }}>Ver todas</Link>
        </div>
        <div className="card-body text-center py-4">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error || licencas.length === 0) {
    return null;
  }

  const formatValidade = (val: string) => (val && val.toUpperCase() !== 'N/A' ? val : '—');

  return (
    <div className="card shadow-sm">
      <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: '#a2449c', color: 'white' }}>
        <h5 className="mb-0">
          <i className="fas fa-id-card me-2"></i>
          Licenças
        </h5>
        <Link to="/licencas" className="btn btn-sm btn-outline-light" style={{ border: 'none' }}>Ver todas</Link>
      </div>
      <div className="card-body">
        <div className="row">
          {licencas.map((lic, idx) => (
            <div key={`${lic.num_licenca}-${lic.num_registo}-${idx}`} className="col-12 mb-3">
              <Link to="/licencas" style={{ textDecoration: 'none' }}>
                <div className="card border-0 shadow-sm" style={{ backgroundColor: '#f8f9fa' }}>
                  <div className="card-body p-3">
                    <div className="fw-bold" style={{ color: '#a2449c' }}>
                      {[lic.nome_cao, lic.nome_registo].filter(Boolean).join(' · ') || '—'}
                    </div>
                    <div className="small text-muted">
                      Nº Registo: {lic.num_registo || '—'} · Nº Licença: {lic.num_licenca || '—'}
                    </div>
                    <div className="small mt-1">
                      Validade: {formatValidade(lic.validade)} ·{' '}
                      <span className={lic.ativa ? 'text-success fw-bold' : 'text-danger'}>{lic.ativa ? 'Válida' : 'Não válida'}</span>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Tipos para o bloco de Ranking ACE (resumo na Home)
interface RankingEntryAce {
  rank: number;
  nomeCao: string;
  nomePessoa: string;
  nomeClube: string;
  pontosObtidos: number;
}

interface RankingCategoriaAce {
  nome: string;
  entries: RankingEntryAce[];
}

// Helper para identificar o mesmo binómio (cão + pessoa)
function rankingEntryKey(e: { nomeCao?: string; nomePessoa?: string }): string {
  return `${(e.nomeCao ?? '').trim()}|${(e.nomePessoa ?? '').trim()}`;
}

// Bloco Ranking Nacional - ACE (estilo igual a Próximas Provas)
const RankingAce: React.FC = () => {
  const [rankings, setRankings] = useState<Record<string, RankingCategoriaAce>>({});
  const [previousRankings, setPreviousRankings] = useState<Record<string, { nome: string; entries: Array<{ rank: number; nomeCao: string; nomePessoa: string }> }>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const ACE_CLUB_MATCH = /ace/i;

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const res = await apiCall('/ranking', { method: 'GET' });
        const raw = (res.rankings || {}) as Record<string, { nome: string; entries: Array<{ rank: number; nomeCao: string; nomePessoa: string; nomeClube: string; pontosObtidos: number }> }>;
        const byCategory: Record<string, RankingCategoriaAce> = {};
        Object.keys(raw).forEach((key) => {
          const cat = raw[key];
          const aceEntries = (cat.entries || []).filter((e) => e.nomeClube && ACE_CLUB_MATCH.test(e.nomeClube));
          if (aceEntries.length > 0) {
            byCategory[key] = {
              nome: cat.nome || key,
              entries: aceEntries.map((e) => ({
                rank: e.rank,
                nomeCao: e.nomeCao,
                nomePessoa: e.nomePessoa,
                nomeClube: e.nomeClube,
                pontosObtidos: e.pontosObtidos ?? 0,
              })),
            };
          }
        });
        setRankings(byCategory);
        setPreviousRankings((res.previousRankings as Record<string, { nome: string; entries: Array<{ rank: number; nomeCao: string; nomePessoa: string }> }>) || {});
      } catch (err: any) {
        setError(err.message || 'Erro ao carregar ranking.');
        setRankings({});
        setPreviousRankings({});
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const previousRankByCategory = useMemo(() => {
    const out: Record<string, Record<string, number>> = {};
    Object.keys(previousRankings).forEach((catKey) => {
      const entries = previousRankings[catKey]?.entries || [];
      out[catKey] = {};
      entries.forEach((e: { rank: number; nomeCao?: string; nomePessoa?: string }) => {
        out[catKey][rankingEntryKey(e)] = e.rank;
      });
    });
    return out;
  }, [previousRankings]);

  const getRankChange = (entry: RankingEntryAce, catKey: string): 'up' | 'down' | null => {
    const prev = previousRankByCategory[catKey]?.[rankingEntryKey(entry)];
    if (prev == null) return null;
    if (entry.rank < prev) return 'up';
    if (entry.rank > prev) return 'down';
    return null;
  };

  const categoryOrder = ['G3_L', 'G3_I', 'G3_M', 'G3_S', 'G2_L', 'G2_I', 'G2_M', 'G2_S', 'INF_G2_I'];
  const orderedCategories = categoryOrder.filter((k) => rankings[k]);

  const getRankBadgeClass = (rank: number) => {
    switch (rank) {
      case 1: return 'badge-rank-1';
      case 2: return 'badge-rank-2';
      case 3: return 'badge-rank-3';
      default: return 'bg-light text-dark border';
    }
  };

  if (loading) {
    return (
      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: '#a2449c', color: 'white' }}>
          <h5 className="mb-0">
            <i className="fas fa-trophy me-2"></i>
            Ranking Nacional - ACE
          </h5>
          <Link to="/ranking" className="btn btn-sm btn-outline-light" style={{ border: 'none' }}>
            Ver todas
          </Link>
        </div>
        <div className="card-body text-center py-4">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: '#a2449c', color: 'white' }}>
          <h5 className="mb-0">
            <i className="fas fa-trophy me-2"></i>
            Ranking Nacional - ACE
          </h5>
          <Link to="/ranking" className="btn btn-sm btn-outline-light" style={{ border: 'none' }}>
            Ver todas
          </Link>
        </div>
        <div className="card-body text-center py-3">
          <p className="text-muted mb-0">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card shadow-sm">
      <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: '#a2449c', color: 'white' }}>
        <h5 className="mb-0">
          <i className="fas fa-trophy me-2"></i>
          Ranking Nacional - ACE
        </h5>
        <Link to="/ranking" className="btn btn-sm btn-outline-light" style={{ border: 'none' }}>
          Ver todas
        </Link>
      </div>
      <div className="card-body">
        {orderedCategories.length === 0 ? (
          <div className="text-center py-3">
            <i className="fas fa-trophy fa-2x mb-2" style={{ color: '#a2449c' }}></i>
            <p className="text-muted mb-0">Nenhum resultado da equipa ACE no ranking.</p>
          </div>
        ) : (
          <div className="row">
            {orderedCategories.map((catKey) => {
              const cat = rankings[catKey];
              return (
                <div key={catKey} className="col-12 mb-3">
                  <div className="card border-0 shadow-sm" style={{ backgroundColor: '#f8f9fa' }}>
                    <div className="card-body p-3">
                      <div className="fw-bold mb-2" style={{ color: '#a2449c' }}>
                        {cat.nome}
                      </div>
                      <ul className="list-unstyled mb-0 small">
                        {cat.entries
                          .sort((a, b) => a.rank - b.rank)
                          .map((entry, idx) => (
                            <li key={`${catKey}-${entry.rank}-${idx}`} className="d-flex justify-content-between align-items-center py-1 border-bottom border-light">
                              <span>
                                <span className="rank-cell-inner me-1" style={{ width: '3.5rem', minWidth: '3.5rem' }}>
                                  <span className={`badge rank-badge-fixed ${getRankBadgeClass(entry.rank)}`}>{entry.rank}º</span>
                                  {getRankChange(entry, catKey) === 'up' && (
                                    <i className="fas fa-arrow-up" style={{ color: 'rgba(25,135,84,0.75)', fontSize: '0.65rem' }} title="Subiu no ranking" aria-hidden />
                                  )}
                                  {getRankChange(entry, catKey) === 'down' && (
                                    <i className="fas fa-arrow-down" style={{ color: 'rgba(220,53,69,0.75)', fontSize: '0.65rem' }} title="Desceu no ranking" aria-hidden />
                                  )}
                                </span>
                                <span className="fw-bold" style={{ color: '#a2449c' }}>{entry.nomeCao}</span>
                                <span className="text-muted"> / {entry.nomePessoa}</span>
                              </span>
                              <span className="fw-bold">{entry.pontosObtidos} pts</span>
                            </li>
                          ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// Componente para mostrar notificação de pagamentos pendentes
const PendingPaymentsNotification: React.FC = () => {
  const { user } = useAuth();
  const [pendingPayments, setPendingPayments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadPendingPayments = async () => {
      if (!user || user.role !== 'student') return;
      
      try {
        setLoading(true);
        setError('');
        
        // Buscar pagamentos pendentes do utilizador
        const response = await apiCall('/payments/user-pending');
        setPendingPayments(response.payments || []);
      } catch (err: any) {
        setError(err.message || 'Erro ao carregar pagamentos pendentes');
      } finally {
        setLoading(false);
      }
    };
    
    loadPendingPayments();
  }, [user]);

  if (loading || error || pendingPayments.length === 0) {
    return null;
  }

  const formatMonth = (monthStr: string) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  };

  const getMonthList = () => {
    const months = pendingPayments.map(payment => formatMonth(payment.month));
    if (months.length === 1) {
      return months[0];
    } else if (months.length === 2) {
      return `${months[0]} e ${months[1]}`;
    } else {
      const lastMonth = months.pop();
      return `${months.join(', ')} e ${lastMonth}`;
    }
  };

  return (
    <div className="row mb-4">
      <div className="col-12">
        <div className="card shadow-sm border-warning">
          <div className="card-header bg-warning text-dark">
            <h5 className="mb-0">
              <i className="fas fa-exclamation-triangle me-2"></i>
              Pagamentos Pendentes
            </h5>
          </div>
          <div className="card-body">
            <div className="alert alert-warning mb-0 mt-3">
              <p className="mb-0">
                <strong>Tem {pendingPayments.length} pagamento{pendingPayments.length > 1 ? 's' : ''} pendente{pendingPayments.length > 1 ? 's' : ''}</strong> referente{pendingPayments.length > 1 ? 's' : ''} ao{pendingPayments.length > 1 ? 's' : ''} mês{pendingPayments.length > 1 ? 'es' : ''} <strong>{getMonthList()}</strong>. 
                Deve consultar "A minha conta" para saber mais detalhes.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Footer: React.FC<{onVersionClick: () => void}> = ({ onVersionClick }) => {
  const [version, setVersion] = useState<string>('');
  useEffect(() => {
    fetch('/changelog.json')
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          setVersion(data[0].version);
        }
      });
  }, []);
  return (
    <footer className="bg-surface-secondary text-center text-muted py-3 mt-auto border-top">
      <div className="container">
        <div className="d-md-flex justify-content-between align-items-center">
          <div className="text-muted small">
            ACE Agility Club &copy; {new Date().getFullYear()} {version && (
              <span style={{ cursor: 'pointer', textDecoration: 'underline', color: '#a2449c', marginLeft: 4 }} onClick={onVersionClick} title="Ver changelog">
                v{version}
              </span>
            )}
          </div>
          <div className="text-muted small">
            developed by <a href="https://isycode.com" target="_blank" rel="noopener noreferrer" style={{ color: '#a2449c', textDecoration: 'none', fontWeight: 600 }}>isycode.com</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

const Home: React.FC = () => {
  const { user } = useAuth();
  const [showICSModal, setShowICSModal] = useState(false);
  const { setLoginResponse } = useContext(LoginResponseContext);
  
  useEffect(() => {
    const checkChangelog = async () => {
      if (user) {
        try {
          const response = await apiCall('/auth/check-changelog', { method: 'GET' });
          if (response.shouldShowChangelog) {
            setLoginResponse({ showChangelog: true });
          }
        } catch (error) {
          console.error('Erro ao verificar changelog:', error);
        }
      }
    };
    
    checkChangelog();
  }, [user, setLoginResponse]);
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="container py-4">
      <div className="row justify-content-center">
        <div className="col-lg-10">
          <div className="text-center mb-5">
            <h1 className="display-4 fw-bold mb-3" style={{color: '#a2449c'}}>
              Bem-vindo ao ACE Agility Club
            </h1>
            <p className="lead text-secondary mb-4">
              Gestão completa de aulas, animais, pagamentos e muito mais!
            </p>
          </div>
          
          {/* Notificação de Pagamentos Pendentes - apenas para alunos */}
          {user.role === 'student' && <PendingPaymentsNotification />}
          
          {/* Bloco Próximas Aulas - apenas para utilizadores logados */}
          {user.role !== 'guest' && (
            <div className="row mb-5">
              <div className="col-12">
                <NextLessons onOpenICSModal={() => setShowICSModal(true)} />
              </div>
            </div>
          )}

          {/* Bloco Próximas Provas */}
          <div className="row mb-5">
            <div className="col-12">
              <NextProvas />
            </div>
          </div>

          {/* Bloco Licenças (apenas do utilizador; escondido se não houver) */}
          <div className="row mb-5">
            <div className="col-12">
              <LicencasUserBlock />
            </div>
          </div>

          {/* Bloco Ranking Nacional - ACE */}
          <div className="row mb-5">
            <div className="col-12">
              <RankingAce />
            </div>
          </div>
          
          {/* Cards de navegação */}
          <div className="row g-4">
            <div className="col-md-4">
              <Link to="/calendar" style={{textDecoration: 'none'}}>
                <div className="card h-100 shadow-sm hover-lift clickable-card">
                  <div className="card-body text-center">
                    <i className="fas fa-calendar-alt fa-3x mb-3" style={{color: '#a2449c'}}></i>
                    <h5 className="card-title" style={{color: '#a2449c'}}>Calendário</h5>
                    <p className="card-text text-muted">
                      Visualize e gerencie todas as suas marcações de aulas
                    </p>
                  </div>
                </div>
              </Link>
            </div>
            {user.role !== 'guest' && (
              <>
                <div className="col-md-4">
                  <Link to="/animals" style={{textDecoration: 'none'}}>
                    <div className="card h-100 shadow-sm hover-lift clickable-card">
                      <div className="card-body text-center">
                        <i className="fas fa-paw fa-3x mb-3" style={{color: '#a2449c'}}></i>
                        <h5 className="card-title" style={{color: '#a2449c'}}>Animais</h5>
                        <p className="card-text text-muted">
                          Gerencie os seus animais e as suas informações
                        </p>
                      </div>
                    </div>
                  </Link>
                </div>
                <div className="col-md-4">
                  <Link to="/statistics" style={{textDecoration: 'none'}}>
                    <div className="card h-100 shadow-sm hover-lift clickable-card">
                      <div className="card-body text-center">
                        <i className="fas fa-chart-bar fa-3x mb-3" style={{color: '#a2449c'}}></i>
                        <h5 className="card-title" style={{color: '#a2449c'}}>Estatísticas</h5>
                        <p className="card-text text-muted">
                          Acompanhe o seu progresso e desempenho
                        </p>
                      </div>
                    </div>
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Modal de Instruções ICS - Fora de qualquer bloco */}
      {showICSModal && (
        <div className="modal fade show d-block" style={{ 
          backgroundColor: 'rgba(0,0,0,0.5)', 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          width: '100%', 
          height: '100%', 
          zIndex: 9999 
        }}>
          <div className="modal-dialog modal-lg" style={{ marginTop: '5vh' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-calendar-plus me-2"></i>
                  Como adicionar o seu calendário de aulas
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowICSModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <h6 className="text-primary mb-3">
                      <i className="fas fa-download me-2"></i>
                      Download Simples
                    </h6>
                    <ol className="mb-4">
                      <li>Clique no botão "Descarregar Calendário" abaixo</li>
                      <li>O ficheiro .ics será descarregado automaticamente</li>
                      <li>Abra o ficheiro no seu calendário preferido:
                        <ul className="mt-2">
                          <li><strong>Google Calendar:</strong> Importar evento</li>
                          <li><strong>Apple Calendar:</strong> Duplo clique no ficheiro</li>
                          <li><strong>Outlook:</strong> Importar calendário</li>
                        </ul>
                      </li>
                    </ol>
                  </div>
                  <div className="col-md-6">
                    <h6 className="text-success mb-3">
                      <i className="fas fa-sync me-2"></i>
                      Subscrição Contínua (Recomendado)
                    </h6>
                    <p className="mb-3">
                      Para ter o seu calendário sempre atualizado automaticamente:
                    </p>
                    <ol className="mb-3">
                      <li>Copie esta URL do seu calendário:</li>
                    </ol>
                    <div className="input-group mb-3">
                      <input 
                        type="text" 
                        className="form-control" 
                        value={`${apiConfig.baseURL}/calendar.ics?aluno=${user?.id}`}
                        readOnly
                      />
                      <button 
                        className="btn btn-outline-primary" 
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(`${apiConfig.baseURL}/calendar.ics?aluno=${user?.id}`);
                          // Mostrar toast de sucesso
                          const toast = document.createElement('div');
                          toast.className = 'alert alert-success position-fixed';
                          toast.style.cssText = 'top: 20px; right: 20px; z-index: 9999;';
                          toast.textContent = 'URL copiada para a área de transferência!';
                          document.body.appendChild(toast);
                          setTimeout(() => document.body.removeChild(toast), 3000);
                        }}
                      >
                        <i className="fas fa-copy"></i>
                      </button>
                    </div>
                    <ol start={2}>
                      <li>Adicione como calendário subscrito no seu calendário:
                        <ul className="mt-2">
                          <li><strong>Google Calendar:</strong> Adicionar calendário → URL</li>
                          <li><strong>Apple Calendar:</strong> Adicionar calendário → URL</li>
                          <li><strong>Outlook:</strong> Adicionar calendário → URL</li>
                        </ul>
                      </li>
                    </ol>
                  </div>
                </div>
                
                <div className="alert alert-info mt-4">
                  <i className="fas fa-info-circle me-2"></i>
                  <strong>Vantagem da subscrição contínua:</strong> As alterações às suas aulas (novas marcações, cancelamentos, alterações de horário) aparecerão automaticamente no seu calendário sem necessidade de reimportar o ficheiro.
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowICSModal(false)}
                >
                  Fechar
                </button>
                <a 
                  href={`${apiConfig.baseURL}/calendar.ics?aluno=${user?.id}`}
                  className="btn btn-primary"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setShowICSModal(false)}
                >
                  <i className="fas fa-calendar-plus me-2"></i>
                  Descarregar Calendário
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ProtectedRoute: React.FC<{ children: React.ReactNode; roles?: string[] }> = ({ children, roles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="text-center">
          <div className="spinner-border mb-3" role="status" style={{ width: '3rem', height: '3rem', color: '#a2449c' }}>
            <span className="visually-hidden">A carregar...</span>
          </div>
          <p className="text-muted">A carregar a aplicação...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const AuthRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">A carregar...</span>
          </div>
          <p className="text-muted">A carregar a aplicação...</p>
        </div>
      </div>
    );
  }

  // Se o utilizador está logado, redirecionar para a página inicial
  if (user) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const PhoneRequiredModal: React.FC<{ show: boolean; onPhoneSaved: () => void }> = ({ show, onPhoneSaved }) => {
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const { updatePhone } = useAuth();
  const { showToast } = React.useContext(ToastContext);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = phone.trim();
    if (!trimmed) {
      showToast('Indique o seu número de telefone.', 'error');
      return;
    }
    const digitsOnly = trimmed.replace(/\D/g, '');
    if (digitsOnly.length < 9) {
      showToast('Número de telefone inválido. Indique pelo menos 9 dígitos.', 'error');
      return;
    }

    setLoading(true);
    try {
      const result = await updatePhone(trimmed);
      if (result.success) {
        showToast('Telefone guardado com sucesso!', 'success');
        setPhone('');
        onPhoneSaved();
      } else {
        showToast(result.message || 'Erro ao guardar telefone', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;
  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1060 }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header" style={{ backgroundColor: '#a2449c' }}>
            <h5 className="modal-title text-white">
              <i className="fas fa-phone me-2"></i>Contacto necessário
            </h5>
          </div>
          <div className="modal-body">
            <div className="text-center mb-3">
              <i className="fas fa-mobile-alt fa-2x mb-2" style={{ color: '#a2449c' }}></i>
              <h6 className="mb-2" style={{ color: '#a2449c' }}>Indique o seu número de telefone</h6>
              <p className="text-muted small mb-0">
                Pedimos o seu contacto para que o treinador o possa contactar em caso de alteração de aulas,
                imprevistos ou outras situações urgentes relacionadas com o treino.
              </p>
            </div>
            <div className="alert alert-warning py-2 mb-3">
              <div className="d-flex align-items-start">
                <i className="fas fa-exclamation-triangle me-2 mt-1"></i>
                <small className="mb-0">
                  Este passo é obrigatório. Só poderá continuar a utilizar a aplicação depois de registar um número de telefone válido.
                </small>
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label htmlFor="requiredPhone" className="form-label">Número de telefone</label>
                <input
                  type="tel"
                  className="form-control"
                  id="requiredPhone"
                  placeholder="Ex: 912 345 678"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <div className="d-grid">
                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ backgroundColor: '#a2449c', border: 'none' }}
                  disabled={loading}
                >
                  {loading ? (
                    <><i className="fas fa-spinner fa-spin me-2"></i>A guardar...</>
                  ) : (
                    <><i className="fas fa-save me-2"></i>Guardar telefone</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

const AnimalRequiredModal: React.FC<{ show: boolean; onAnimalAdded: () => void }> = ({ show, onAnimalAdded }) => {
  const [form, setForm] = useState({ name: '', birth_date: '', observations: '' });
  const [loading, setLoading] = useState(false);
  const { showToast } = React.useContext(ToastContext);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.birth_date) {
      showToast('Nome e data de nascimento são obrigatórios.', 'error');
      return;
    }
    setLoading(true);
    try {
      const data = await apiCall('/animals', {
        method: 'POST',
        body: JSON.stringify(form)
      });
      if (data.animal) {
        showToast(`Animal "${form.name}" registado com sucesso! Agora já pode marcar aulas.`, 'success');
        setForm({ name: '', birth_date: '', observations: '' });
        setTimeout(() => {
          onAnimalAdded();
        }, 1200);
      }
    } catch (err: any) {
      showToast(err.message || 'Erro ao adicionar animal', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;
  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header" style={{backgroundColor: '#a2449c'}}>
            <h5 className="modal-title text-white">
              <i className="fas fa-paw me-2"></i>Bem-vindo ao ACE!
            </h5>
          </div>
          <div className="modal-body">
            <div className="text-center mb-3">
              <i className="fas fa-dog fa-2x mb-2" style={{color: '#a2449c'}}></i>
              <h6 className="mb-2" style={{color: '#a2449c'}}>Primeiro Passo: Registar o seu Animal</h6>
              <p className="text-muted small mb-0">Para poder marcar aulas, precisa de registar pelo menos um animal.</p>
            </div>
            <div className="alert alert-info py-2 mb-3">
              <div className="d-flex align-items-center">
                <i className="fas fa-info-circle me-2 text-info"></i>
                <small className="mb-0">O treinador precisa de saber qual animal vai participar na aula.</small>
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label htmlFor="firstAnimalName" className="form-label">Nome do Animal:</label>
                <input type="text" className="form-control" id="firstAnimalName" name="name" placeholder="Ex: Max, Luna, Rex..." value={form.name} onChange={handleChange} required autoFocus />
              </div>
              <div className="mb-3">
                <label htmlFor="firstAnimalBirthDate" className="form-label">Data de Nascimento:</label>
                <input type="date" className="form-control" id="firstAnimalBirthDate" name="birth_date" value={form.birth_date} onChange={handleChange} required />
              </div>
              <div className="mb-3">
                <label htmlFor="firstAnimalObservations" className="form-label">Observações (opcional):</label>
                <textarea className="form-control" id="firstAnimalObservations" name="observations" rows={2} placeholder="Escreva aqui observações sobre o animal..." value={form.observations} onChange={handleChange}></textarea>
              </div>
              <div className="d-grid">
                <button type="submit" className="btn btn-primary" style={{ backgroundColor: '#a2449c', border: 'none' }} disabled={loading}>
                  {loading ? <><i className="fas fa-spinner fa-spin me-2"></i>A registar...</> : <><i className="fas fa-plus me-2"></i>Registar Animal</>}
                </button>
              </div>
            </form>
            <div className="text-center mt-3">
              <small className="text-muted">
                <i className="fas fa-lightbulb me-1"></i>
                Pode registar vários animais e escolher qual vai participar em cada aula!
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  const { user } = useAuth();
  const [showAnimalModal, setShowAnimalModal] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [showChangelog, setShowChangelog] = useState(false);
  const [loginResponse, setLoginResponse] = useState<{ showChangelog?: boolean } | null>(null);

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    setToast({ message, type });
  };

  const needsPhone =
    !!user &&
    user.role === 'student' &&
    (!user.phone || !String(user.phone).trim());

  useEffect(() => {
    setShowPhoneModal(needsPhone);
  }, [needsPhone]);

  useEffect(() => {
    const checkAnimals = async () => {
      // Só pedir animal depois de ter telefone (ou se não for aluno)
      if (user && user.role === 'student' && !needsPhone) {
        try {
          const data = await apiCall('/animals/my');
          if (!data.animals || data.animals.length === 0) {
            setShowAnimalModal(true);
          }
        } catch {}
      }
    };
    checkAnimals();
  }, [user, needsPhone]);

  // Mostrar changelog automaticamente se o login indicar que deve ser mostrado
  useEffect(() => {
    if (loginResponse?.showChangelog) {
      setShowChangelog(true);
      setLoginResponse(null); // Reset para evitar mostrar novamente
    }
  }, [loginResponse]);

  return (
    <ToastContext.Provider value={{ showToast }}>
      <LoginResponseContext.Provider value={{ setLoginResponse }}>
        <Router>
          <div className="d-flex flex-column min-vh-100 bg-surface">
            <Navbar />
            <InvalidLicensesAlertBar />
            <main className="flex-fill">
              {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
              <PhoneRequiredModal show={showPhoneModal} onPhoneSaved={() => setShowPhoneModal(false)} />
              <AnimalRequiredModal show={showAnimalModal && !showPhoneModal} onAnimalAdded={() => setShowAnimalModal(false)} />
            <Routes>
              <Route path="/" element={<Home />} />
              
              {/* Rotas de autenticação - apenas para utilizadores não logados */}
              <Route path="/login" element={
                <AuthRoute>
                  <LoginPage />
                </AuthRoute>
              } />
              <Route path="/register" element={
                <AuthRoute>
                  <RegisterPage />
                </AuthRoute>
              } />
              <Route path="/forgot-password" element={
                <AuthRoute>
                  <ForgotPasswordPage />
                </AuthRoute>
              } />
              
              {/* Rotas protegidas - apenas para utilizadores logados */}
              <Route path="/animals" element={
                <ProtectedRoute>
                  <AnimalsPage />
                </ProtectedRoute>
              } />
              <Route path="/calendar" element={
                <ProtectedRoute>
                  <CalendarPage />
                </ProtectedRoute>
              } />
              <Route path="/availability" element={
                <ProtectedRoute roles={['trainer']}>
                  <AvailabilityPage />
                </ProtectedRoute>
              } />
              <Route path="/payments" element={
                <ProtectedRoute roles={['trainer']}>
                  <PaymentsPage />
                </ProtectedRoute>
              } />
              <Route path="/statistics" element={
                <ProtectedRoute>
                  <StatisticsPage />
                </ProtectedRoute>
              } />
              <Route path="/account" element={
                <ProtectedRoute roles={['student', 'external']}>
                  <AccountPage />
                </ProtectedRoute>
              } />
              <Route path="/settings" element={
                <ProtectedRoute roles={['trainer']}>
                  <SettingsPage />
                </ProtectedRoute>
              } />
              <Route path="/weather" element={
                <ProtectedRoute>
                  <WeatherPage />
                </ProtectedRoute>
              } />
              <Route path="/calendar-provas" element={
                <ProtectedRoute>
                  <CalendarProvasPage />
                </ProtectedRoute>
              } />
              <Route path="/ranking" element={
                <ProtectedRoute>
                  <RankingPage />
                </ProtectedRoute>
              } />
              <Route path="/licencas" element={
                <ProtectedRoute>
                  <LicencasPage />
                </ProtectedRoute>
              } />
              <Route path="/help" element={
                <ProtectedRoute roles={['student']}>
                  <HelpPage />
                </ProtectedRoute>
              } />
              <Route path="/students" element={
                <ProtectedRoute roles={['trainer']}>
                  <StudentsPage />
                </ProtectedRoute>
              } />
            </Routes>
          </main>
          <Footer onVersionClick={() => setShowChangelog(true)} />
          <ChangelogModal open={showChangelog} onClose={() => setShowChangelog(false)} />
        </div>
        </Router>
      </LoginResponseContext.Provider>
    </ToastContext.Provider>
  );
};

export default App;
